import React, { FC, ReactNode, useEffect } from 'react';

interface DrawerPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Reusable right-side drawer panel — 340px wide, full viewport height.
 * Backdrop click or Escape key closes. Locks body scroll while open.
 *
 * Spec: documentations/AidFone_Johnley_Briefing_March2026.docx §2
 */
export const DrawerPanel: FC<DrawerPanelProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
}) => {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Lock body scroll + mark body as drawer-open so the AidFone assistant
  // bubble (and anything else that wants to respond) can hide itself.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('drawer-open');
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove('drawer-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div
        data-testid="drawer-backdrop"
        className="fixed inset-0 bg-black/20 z-40 transition-opacity"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-label={title}
        className="fixed top-0 right-0 bottom-0 w-[340px] max-w-[100vw] bg-white dark:bg-slate-900 shadow-2xl z-50 flex flex-col border-l border-brand-border dark:border-slate-800 animate-[slideInRight_200ms_ease-out]"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-brand-border dark:border-slate-800">
          <h2 className="text-lg font-display font-bold text-brand-text dark:text-white">
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close panel"
            className="p-2 rounded-lg hover:bg-brand-bg-warm dark:hover:bg-slate-800 transition-colors"
          >
            <span className="material-symbols-outlined text-brand-text-muted">close</span>
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {footer && (
          <div className="border-t border-brand-border dark:border-slate-800 p-5">{footer}</div>
        )}
      </aside>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

export default DrawerPanel;
